import type { VerrichtingenResponse } from '@ksyos/http';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Card, Divider, FormParagraph, Link, PageLayout, Spinner, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ConclusieDiagnoseOption } from '../../../../../common/enums/ConclusieDiagnoseOption';
import type { IReduxState } from '../../../../configureStore';
import { servicedeskPhoneNumber } from '../../../../../common/constants';
import { isConclusieDiagnoseOption } from '../../../../../common/enums/ConclusieDiagnoseOption';
import { PatientDataError } from '../../../../../common/enums/PatientDataError';
import { ServiceCode } from '../../../../../common/enums/ServiceCode';
import { mijnKsyosClient } from '../../../../services/api/mijnKsyosClient';
import { useDefinedContext } from '../../../../utils/hooks/useDefinedContext';
import { LatestCarePathContext } from '../../../pages/LatestCarePathContext';
import { WelcomeHero } from '../../molecules/welcomeHero/WelcomeHero';
import { OptometryStepsCard } from '../toCards/OptometryStepsCard';
import { ResultCard } from '../toCards/ResultCard';
import { ToGeneralInfoCard } from '../toCards/ToGeneralInfoCard';

type WelcomeProps = Readonly<{
    error?: PatientDataError;
}>;

export function NewWelcome(props: WelcomeProps) {
    const { error } = props;
    const { serviceCode } = useDefinedContext(LatestCarePathContext);
    const patientName = useSelector((state: IReduxState) => state.authentication.user?.displayName);
    const [isLoading, setIsLoading] = useState(true);
    const [result, setResult] = useState<ConclusieDiagnoseOption>();
    const serviceGroupName = serviceCode === ServiceCode.Optometry ? 'Optometrisch onderzoek' : 'Oog algemeen';
    const hasResult = isConclusieDiagnoseOption(result);

    useEffect(() => {
        datadogRum.setViewContextProperty('serviceGroupName', serviceGroupName);
        getResult(serviceGroupName)
            .then(setResult)
            .finally(() => setIsLoading(false));
    }, [serviceGroupName]);

    return (
        <PageLayout width="small">
            <Box py={4}>
                <Text fontWeight="bold">Goededag {patientName ?? ''},</Text>
                <Text>Dit is je persoonlijke Ksyos-omgeving</Text>
                {error === PatientDataError.generalError && (
                    <FormParagraph>
                        <Text color="warning">Er is iets fout gegaan</Text>
                        <Text>
                            De persoonlijke omgeving kon niet succesvol worden ingeladen. Neem voor hulp contact op met
                            de Ksyos servicedesk
                        </Text>
                        <Link
                            openInNewTab={true}
                            to={`tel:${servicedeskPhoneNumber}`}
                            variant="link"
                        >
                            {servicedeskPhoneNumber}
                        </Link>
                    </FormParagraph>
                )}
                {error === PatientDataError.noDataAvailable && (
                    <FormParagraph>Op dit moment zijn er geen (actieve) onderzoeken.</FormParagraph>
                )}
            </Box>
            <WelcomeHero
                imgUrlDesktop="/assets/images/thuisconsult-home-large.svg"
                imgUrlMobile="/assets/images/thuisconsult-home-small.svg"
            />
            {isLoading ? (
                <Card>
                    <Card.Body>
                        <Box
                            alignItems="center"
                            display="flex"
                            justifyContent="center"
                            minHeight="50px"
                        >
                            <Spinner />
                        </Box>
                    </Card.Body>
                </Card>
            ) : (
                <Card>
                    <Card.Body>
                        {hasResult ? (
                            <ResultCard
                                isInitiallyOpen={true}
                                result={result}
                            />
                        ) : (
                            <OptometryStepsCard isInitiallyOpen={true} />
                        )}
                        <Divider spacing="small" />
                        <ToGeneralInfoCard />
                    </Card.Body>
                </Card>
            )}
        </PageLayout>
    );
}

async function getResult(serviceGroupName?: string): Promise<ConclusieDiagnoseOption | undefined> {
    const verrichtingen = await mijnKsyosClient.verrichtingen.getVerrichtingen({ query: { serviceGroupName } });
    if (verrichtingen.status !== 200) {
        return;
    }

    return getLatestResult(verrichtingen.body.verrichtingen);
}

function getLatestResult(verrichtingen: VerrichtingenResponse['verrichtingen']): ConclusieDiagnoseOption | undefined {
    const latestVerrichting = verrichtingen.at(-1);

    if (
        latestVerrichting?.type === 'toc-diagnostic-report-consult-report' &&
        isConclusieDiagnoseOption(latestVerrichting.data.heb_je_een_diagnose)
    ) {
        return latestVerrichting.data.heb_je_een_diagnose;
    }
    if (
        latestVerrichting?.type === 'too-diagnostic-report-examination-report' &&
        isConclusieDiagnoseOption(latestVerrichting.data.conclusie_diagnose)
    ) {
        return latestVerrichting.data.conclusie_diagnose;
    }
    if (
        (latestVerrichting?.type === 'optometry-examination' || latestVerrichting?.type === 'optometry-consult') &&
        isConclusieDiagnoseOption(latestVerrichting.data.diagnose)
    ) {
        return latestVerrichting.data.diagnose;
    }
}
